/**
 * Pure, dependency-free helpers for the comments/notes feature (T-057, frontend
 * half). Kept separate from CommentExtension.js and CommentsPanel.js so they are
 * unit-testable without mounting TipTap or React.
 */

/**
 * Decide whether a new comment anchors to a text RANGE (mark, wraps the
 * selected text) or a cursor POINT (atom node, for a collapsed selection).
 * Mirrors ProseMirror's own convention: from === to means collapsed.
 *
 * Args:
 *   selection (object): { from, to } (ProseMirror-shaped selection, or null)
 *
 * Returns:
 *   'range' | 'point'
 */
export function decideAnchorMode(selection) {
  if (!selection) return 'point';
  const { from, to } = selection;
  return typeof to === 'number' && typeof from === 'number' && to > from ? 'range' : 'point';
}

/**
 * Format the quoted anchor text for display in the panel. Comments without an
 * anchor_text (sin selección) are generales del documento and show a fixed
 * placeholder instead of a quote.
 *
 * Args:
 *   anchorText (string|null|undefined)
 *   maxLen (number): truncation threshold (default 80)
 *
 * Returns:
 *   string: quoted/truncated citation, or the placeholder
 */
export function formatAnchorCitation(anchorText, maxLen = 80) {
  if (!anchorText) return '(comentario general)';
  const trimmed = String(anchorText).trim();
  if (!trimmed) return '(comentario general)';
  if (trimmed.length <= maxLen) return `"${trimmed}"`;
  return `"${trimmed.slice(0, maxLen - 1)}…"`;
}

/**
 * Sort comment threads for the panel list: open threads first (newest first),
 * then resolved threads (newest first). Keeps the active work at the top
 * while resolved history stays available below.
 *
 * Args:
 *   list (array): thread objects with {status, created_at}
 *
 * Returns:
 *   array: new sorted array (input is not mutated)
 */
export function sortThreads(list) {
  if (!Array.isArray(list)) return [];
  const byNewest = (a, b) => new Date(b.created_at) - new Date(a.created_at);
  const open = list.filter((t) => t.status !== 'resolved').sort(byNewest);
  const resolved = list.filter((t) => t.status === 'resolved').sort(byNewest);
  return [...open, ...resolved];
}

/**
 * Count replies on a thread (defensive against missing/non-array replies).
 */
export function countReplies(thread) {
  return Array.isArray(thread && thread.replies) ? thread.replies.length : 0;
}
